import type { User } from '@/@types/user'
import { FOLLOW_USER, UNFOLLOW_USER } from '@/constants/api'
import { followUser, getUserQueryOptions, unfollowUser } from '@/utils/fetchers/user'
import useUser from '@/utils/hooks/useUser'
import { sleep } from '@/utils/promise'
import queryClient from '@/utils/queryClient'
import { Button, type ButtonProps } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import type { MouseEvent } from 'react'
import { useCallback } from 'react'
import { useAsync } from 'react-use'
import styles from './FollowToggleButton.module.css'
import { showConfirm } from './Modal/utils'

export interface FollowToggleButtonProps extends ButtonProps {
	data: User
	noAddIcon?: boolean
}

export default function FollowToggleButton({
	data,
	noAddIcon = false,
	className,
	...props
}: FollowToggleButtonProps) {
	const currentUser = useUser(void 0)
	const isCurrentUser = currentUser?.id === data.id
	const isFollowing = data.following === true

	const revalidateUser = useCallback(
		() => queryClient.invalidateQueries({ queryKey: getUserQueryOptions(data.username).queryKey }),
		[data.username]
	)
	const { mutateAsync: follow, isPending: isFollowingPending } = useMutation({
		mutationKey: [FOLLOW_USER, data.id],
		mutationFn: (username: string) => followUser(username),
		onSettled: revalidateUser,
	})
	const { mutateAsync: unfollow, isPending: isUnfollowingPending } = useMutation({
		mutationKey: [UNFOLLOW_USER, data.id],
		mutationFn: (username: string) => unfollowUser(username),
		onSettled: revalidateUser,
	})
	const { value: isLoading = false } = useAsync(
		async () => sleep(250).then(() => isFollowingPending || isUnfollowingPending),
		[isFollowingPending, isUnfollowingPending]
	)
	const handleFollowToggle = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation()
			if (isFollowing) {
				showConfirm('确定取消关注吗？', void 0, {
					confirmProps: {
						children: '不再关注',
					},
				}).then(() => unfollow(data.username))
			} else {
				follow(data.username)
			}
		},
		[data.username, follow, isFollowing, unfollow]
	)

	if (isCurrentUser) {
		return null
	}

	return (
		<AnimatePresence mode="wait">
			{isFollowing ? (
				<motion.div
					key="followed"
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					exit={{
						opacity: 0,
					}}
				>
					<Button
						loading={isLoading}
						disabled={isLoading}
						onClick={handleFollowToggle}
						variant="outline"
						className={clsx(styles.followedButton, className)}
						{...props}
					>
						已关注
					</Button>
				</motion.div>
			) : (
				<motion.div
					key="follow"
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					exit={{
						opacity: 0,
					}}
				>
					<Button
						loading={isLoading}
						disabled={isLoading}
						onClick={handleFollowToggle}
						className={clsx(styles.followButton, className)}
						{...props}
					>
						{!noAddIcon && (
							<svg
								width="17"
								height="16"
								viewBox="0 0 17 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M3.41675 8H14.0834"
									stroke="currentColor"
									strokeWidth="1.66667"
									strokeLinecap="round"
								/>
								<path
									d="M8.75 2.6665L8.75 13.3332"
									stroke="currentColor"
									strokeWidth="1.66667"
									strokeLinecap="round"
								/>
							</svg>
						)}
						关注
					</Button>
				</motion.div>
			)}
		</AnimatePresence>
	)
}
